import React from 'react';
import { Box, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { SectionCard } from '../pagekit';
import { ScrollTable, StatTile, TierChip } from './consoleUi';

const TIER_ORDER = ['enterprise', 'pro-plus', 'pro', 'free'];

/**
 * Sorted [key, count] pairs.
 * @param {Record<string, number>|undefined} map - Counter.
 * @returns {Array<[string, number]>} Entries, largest first.
 */
const entries = (map) => Object.entries(map || {}).sort((a, b) => b[1] - a[1]);

/**
 * Tier rows in a fixed order.
 * @param {Record<string, number>|undefined} map - Tier counter.
 * @returns {Array<[string, number]>} Entries.
 */
const tierEntries = (map) => {
  const known = TIER_ORDER.filter((tier) => map?.[tier]).map((tier) => [tier, map[tier]]);
  const other = Object.entries(map || {}).filter(([tier]) => !TIER_ORDER.includes(tier));
  return [...known, ...other];
};

/**
 * Overview tab: platform counts.
 * @param {{data: object}} props - Overview payload from platform-admin.
 * @returns {JSX.Element} Panel.
 */
export default function ConsoleOverview({ data }) {
  const tenants = data?.tenants || {};
  const users = data?.users || {};
  const memberships = data?.memberships || {};
  const paid = Object.entries(tenants.byTier || {}).filter(([tier]) => tier !== 'free').reduce((sum, [, n]) => sum + n, 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        <StatTile label="Tenants" value={tenants.total ?? 0} hint={`${paid} on a paid tier`} />
        <StatTile label="Users" value={users.total ?? 0} hint={`${users.confirmed ?? 0} confirmed`} />
        <StatTile label="Signed in, 7 days" value={users.signedIn7d ?? 0} hint={`${users.signedIn30d ?? 0} in 30 days`} />
        <StatTile label="Used AI, 7 days" value={users.aiActive7d ?? 0} hint={`${users.aiActive30d ?? 0} in 30 days`} />
        <StatTile label="Subscriptions" value={tenants.withSubscription ?? 0} hint={`${tenants.withStripeCustomer ?? 0} Stripe customers`} />
        <StatTile label="Memberships" value={memberships.total ?? 0} hint={`${tenants.personal ?? 0} personal workspaces`} />
        <StatTile label="Users without a tenant" value={users.withoutMembership ?? 0} hint="No active membership" />
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
        <SectionCard title="Tenants by tier" description="Highest plan held by an active member.">
          <ScrollTable label="Tenants by tier" minWidth={280} maxHeight={320}>
            <TableHead>
              <TableRow>
                <TableCell>Tier</TableCell>
                <TableCell align="right">Tenants</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tierEntries(tenants.byTier).map(([tier, count]) => (
                <TableRow key={tier}>
                  <TableCell><TierChip tier={tier} /></TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
        </SectionCard>

        <SectionCard title="Memberships by plan" description="memberships.plan_name across every tenant.">
          <ScrollTable label="Memberships by plan" minWidth={280} maxHeight={320}>
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell align="right">Memberships</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries(memberships.byPlan).map(([plan, count]) => {
                const tier = memberships.planTiers?.[plan] || 'free';
                return (
                  <TableRow key={plan}>
                    <TableCell>{plan}</TableCell>
                    <TableCell><TierChip tier={tier} /></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{count}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </ScrollTable>
        </SectionCard>

        <SectionCard title="Memberships by role">
          <ScrollTable label="Memberships by role" minWidth={280} maxHeight={320}>
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell align="right">Memberships</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries(memberships.byRole).map(([role, count]) => (
                <TableRow key={role}>
                  <TableCell>{role.replace(/_/g, ' ')}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
        </SectionCard>

        <SectionCard title="Memberships by status">
          <ScrollTable label="Memberships by status" minWidth={280} maxHeight={320}>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell align="right">Memberships</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries(memberships.byStatus).map(([status, count]) => (
                <TableRow key={status}>
                  <TableCell>{status}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
        </SectionCard>
      </Box>
    </Box>
  );
}
